import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { getShopHours, saveShopHours } from '../services/shopHours';

const daysOfWeek = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const ShopHoursDialog = ({ open, onClose, shopId }) => {
  const [hours, setHours] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingDays, setSavingDays] = useState({});

  useEffect(() => {
    if (open && shopId) {
      fetchHours();
    }
  }, [open, shopId]);

  const fetchHours = async () => {
    setLoading(true);
    try {
      const shopHours = await getShopHours(shopId);
      const initializedHours = {};
      daysOfWeek.forEach(day => {
        initializedHours[day] = (shopHours && shopHours[day]) || { id: null, from: '', to: '', closed: true };
      });
      setHours(initializedHours);
    } catch (error) {
      console.error('Failed to fetch shop hours:', error);
      const defaultHours = {};
      daysOfWeek.forEach(day => {
        defaultHours[day] = { id: null, from: '', to: '', closed: true };
      });
      setHours(defaultHours);
    } finally {
      setLoading(false);
    }
  };

  const handleTimeChange = (day, field, value) => {
    setHours((prev) => ({
      ...prev,
      [day]: { ...prev[day], [field]: value },
    }));
  };

  const handleClosedChange = (day, checked) => {
    setHours((prev) => ({
      ...prev,
      [day]: { ...prev[day], closed: checked },
    }));
  };

  const handleSaveSingleDay = async (day) => {
    const dayHours = hours[day];
    if (!dayHours) {
      console.error('No hours data for', day);
      return;
    }

    setSavingDays(prev => ({ ...prev, [day]: true }));
    try {
      // Automatically chooses POST (create) or PUT (update) based on ID
      const result = await saveShopHours(shopId, day, dayHours);
      
      // Update the ID if a new record was created
      if (result?.data?.id && !dayHours.id) {
        setHours(prev => ({
          ...prev,
          [day]: { ...prev[day], id: result.data.id }
        }));
      }
    } catch (error) {
      console.error('Failed to save', day, error);
    } finally {
      setSavingDays(prev => ({ ...prev, [day]: false }));
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3 }
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid #e5e7eb', pb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937' }}>
          Shop Hours
        </Typography>
        <Typography variant="body2" sx={{ color: '#6b7280', mt: 0.5 }}>
          Set the operating hours for each day of the week
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: '#52ad83' }} />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: '#374151', width: '18%', py: 2 }}>
                    Day
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151', width: '22%', py: 2 }}>
                    Opening Time
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151', width: '22%', py: 2 }}>
                    Closing Time
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151', width: '20%', py: 2 }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151', width: '18%', py: 2 }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {daysOfWeek.map((day) => (
                  <TableRow 
                    key={day}
                    sx={{ 
                      '&:nth-of-type(even)': { backgroundColor: '#f9fafb' },
                      '&:last-child td': { borderBottom: 0 }
                    }}
                  >
                    <TableCell sx={{ verticalAlign: 'middle', py: 2 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#1f2937' }}>
                        {day}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'middle', py: 2 }}>
                      <TextField
                        type="time"
                        value={hours[day]?.from || ''}
                        onChange={(e) => handleTimeChange(day, 'from', e.target.value)}
                        disabled={hours[day]?.closed || savingDays[day]}
                        fullWidth
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'middle', py: 2 }}>
                      <TextField
                        type="time"
                        value={hours[day]?.to || ''}
                        onChange={(e) => handleTimeChange(day, 'to', e.target.value)}
                        disabled={hours[day]?.closed || savingDays[day]}
                        fullWidth
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                          },
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'middle', py: 2 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={hours[day]?.closed || false}
                            onChange={(e) => handleClosedChange(day, e.target.checked)}
                            disabled={savingDays[day]}
                            sx={{
                              color: '#6b7280',
                              '&.Mui-checked': {
                                color: '#52ad83',
                              },
                            }}
                          />
                        }
                        label={
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: hours[day]?.closed ? '#dc2626' : '#6b7280',
                              fontWeight: hours[day]?.closed ? 500 : 400
                            }}
                          >
                            {hours[day]?.closed ? 'Closed' : 'Open'}
                          </Typography>
                        }
                      />
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'middle', py: 2 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleSaveSingleDay(day)}
                        disabled={savingDays[day]}
                        sx={{
                          textTransform: 'none',
                          borderColor: '#52ad83',
                          color: '#52ad83',
                          '&:hover': {
                            borderColor: '#3d8f64',
                            backgroundColor: 'rgba(82, 173, 131, 0.08)',
                          },
                          '&:disabled': {
                            borderColor: '#d1d5db',
                            color: '#9ca3af',
                          }
                        }}
                      >
                        {savingDays[day] ? <CircularProgress size={16} /> : 'Save'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
        <Button 
          onClick={onClose} 
          disabled={saving}
          sx={{ 
            textTransform: 'none',
            color: '#6b7280',
            '&:hover': { background: '#f3f4f6' }
          }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShopHoursDialog;

